<script setup>
import { computed, useAttrs, useId } from "vue";

defineOptions({ inheritAttrs: false });
defineProps({
  modelValue: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(["update:modelValue", "change"]);

const attrs = useAttrs();
const rootClass = computed(() => attrs.class);
const rootStyle = computed(() => attrs.style);
const inputAttrs = computed(() => {
  const { class: _class, style: _style, ...rest } = attrs;
  return rest;
});

const descriptionId = useId();

function onChange(e) {
  const checked = e.target.checked;
  emit("update:modelValue", checked);
  emit("change", checked);
}
</script>

<template>
  <div class="checkbox-row" :class="[rootClass, { disabled: disabled }]" :style="rootStyle">
    <label class="checkbox">
      <input
        type="checkbox"
        :checked="modelValue"
        :disabled
        :aria-describedby="$slots['label-info'] ? descriptionId : undefined"
        class="input"
        v-bind="inputAttrs"
        @change="onChange"
      />
      <span class="check">
        <svg
          v-if="modelValue"
          viewBox="0 0 12 10"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="1 5 4.5 8.5 11 1.5" />
        </svg>
      </span>
      <span v-if="$slots.default" class="label"><slot /></span>
    </label>
    <slot v-if="$slots['label-info']" :id="descriptionId" name="label-info" />
  </div>
</template>

<style lang="scss" scoped>
@use "@/colors";
@use "@/styles/mixins/typography";

// Block-level — an inline-flex root would let `.check`'s baseline shift
// (when the SVG checkmark mounts) propagate to the parent's inline line and
// visibly jump the row on click.
.checkbox-row {
  position: relative;
  display: flex;
  width: fit-content;
  align-items: center;
  gap: 0.375rem;

  &.disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
}

.checkbox {
  display: inline-flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;

  .disabled & {
    cursor: not-allowed;
  }
}

.input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;

  &:focus-visible + .check,
  &.focus-visible + .check {
    outline: 2px solid colors.$main;
    outline-offset: 2px;
  }

  &:checked + .check {
    background-color: colors.$main;
    border-color: colors.$main;
    color: colors.$white;
  }
}

.check {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: 1.5px solid colors.$border-light-color;
  border-radius: 4px;
  background-color: colors.$white;
  flex-shrink: 0;

  svg {
    width: 10px;
    height: 10px;
  }
}

.label {
  @include typography.small("regular");

  color: colors.$text-color;
}
</style>
